'use client';

import { COMPANY_COUNTRIES, SWISS_CANTON_NAMES, isCompanyCountryCode, swissCantonLabel } from '@/lib/countries';
import { z } from 'zod';
import type { i18n as I18n } from 'i18next';
import { FALLBACK_RANGE_EXTENTS, searchRangeExtentsSchema } from '@/lib/range-slider-scale';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type FilterTier, controlLock, groupLock as groupLockFor } from '@/lib/search-gating';
import { RECHTSFORM_OPTIONS, bundeslandList, courtList as COURT_LIST_SEED, sortByFacetCount } from './searchConstants';
import { allIndustryCategoriesSorted } from '@/lib/industry-categories';
import { MEMBERSHIP_OPTIONS, REGISTER_TYPE_OPTIONS, naceSortKey, type NaceCode } from './searchSidebarOptions';
import { getNaceParent, visibleNaceCodes } from './searchSidebarHelpers';

/**
 * Per-option company counts (mv_search_facets) and the range-slider extents.
 */
export function useSearchFacets() {
  // Per-option company counts (mv_search_facets). Absent until the fetch
  // lands, and absent forever on a DB without migration 305 — every consumer
  // treats "no count" as unknown and renders as before, so this is decoration
  // that can never break the sidebar. Declared before the option lists
  // because several of them are now *ordered* by these counts.
  const [extents, setExtents] = useState(FALLBACK_RANGE_EXTENTS);
  const [facets, setFacets] = useState<Record<string, Record<string, number>>>({});
  useEffect(() => {
    fetch('/api/search/filters/facets')
      .then((res) => res.json())
      .then((data) => {
        const parsed = z.object({ facets: z.record(z.string(), z.record(z.string(), z.number())).optional(), extents: searchRangeExtentsSchema.optional() }).safeParse(data);
        if (parsed.success) {
          if (parsed.data.facets) setFacets(parsed.data.facets);
          if (parsed.data.extents) setExtents(parsed.data.extents);
        }
      })
      .catch(() => {});
  }, []);
  const facetCount = useCallback(
    (facet: string, value: string): number | undefined => facets[facet]?.[value],
    [facets],
  );
  /** Sum of several options — Rechtsform maps one label onto several codes. */
  const facetSum = useCallback(
    (facet: string, values: string[]): number | undefined => {
      const group = facets[facet];
      if (!group) return undefined;
      const present = values.filter((v) => v in group);
      if (present.length === 0) return undefined;
      return present.reduce((n, v) => n + group[v], 0);
    },
    [facets],
  );
  return { extents, facets, facetCount, facetSum };
}

/**
 * UI-only state of the pickers: which dropdown is open, its search text, the
 * expanded NACE nodes, the accordion's open group, and the CPV/court lists
 * the pickers offer. None of it is a filter value.
 */
export function useFilterPickerState() {
  const [cityOpen, setCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [bundeslandOpen, setBundeslandOpen] = useState(false);
  const [ageOpen, setAgeOpen] = useState(false);
  const [naceCodesOpen, setNaceCodesOpen] = useState(false);
  const [naceSearch, setNaceSearch] = useState('');
  const [expandedNaceNodes, setExpandedNaceNodes] = useState<Set<string>>(
    () => new Set(),
  );
  const naceListRef = useRef<HTMLDivElement>(null);
  const [industryOpen, setIndustryOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [cpvCodesOpen, setCpvCodesOpen] = useState(false);
  const [cpvSearch, setCpvSearch] = useState('');
  const [cpvCodesList, setCpvCodesList] = useState<{ code: string; description: string }[]>([]);
  const cpvListRef = useRef<HTMLDivElement>(null);
  const cpvDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load top-level CPV codes on mount, then narrow on search
  useEffect(() => {
    fetch('/api/search/cpv-codes')
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (cpvDebounceRef.current) clearTimeout(cpvDebounceRef.current);
    if (cpvSearch.length >= 2) {
      cpvDebounceRef.current = setTimeout(() => {
        fetch(`/api/search/cpv-codes?query=${encodeURIComponent(cpvSearch)}`)
          .then((res) => res.json())
          .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
          .catch(() => {});
      }, 200);
    } else if (cpvSearch.length === 0) {
      fetch('/api/search/cpv-codes')
        .then((res) => res.json())
        .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
        .catch(() => {});
    }
    return () => { if (cpvDebounceRef.current) clearTimeout(cpvDebounceRef.current); };
  }, [cpvSearch]);

  const [rechtsformOpen, setRechtsformOpen] = useState(false);
  const [yearPickerOpen, setYearPickerOpen] = useState(false);
  const [registerTypeOpen, setRegisterTypeOpen] = useState(false);
  const [registerCourtOpen, setRegisterCourtOpen] = useState(false);
  const [registerCourtSearch, setRegisterCourtSearch] = useState('');

  // Seeded from the static list so the dropdown renders instantly, then
  // replaced by the live set from mv_search (ordered by company count) so
  // the options can never drift from what is actually searchable.
  const [courtList, setCourtList] = useState<string[]>(COURT_LIST_SEED);
  useEffect(() => {
    fetch('/api/search/filters/courts')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data?.courts) && data.courts.length > 0) {
          setCourtList(data.courts);
        }
      })
      .catch(() => {});
  }, []);
  // Accordion state: at most one filter group expanded at a time. Setting to
  // null collapses every group. Stored at the sidebar level so opening one
  // group implicitly closes any other.
  //
  // Company profile starts open: every filter in it is usable without an
  // account, so a first-time visitor lands on something they can act on
  // rather than on six collapsed headers, two of which are locked.
  const [openFilterGroup, setOpenFilterGroup] = useState<string | null>(
    'profile',
  );
  const handleGroupOpenChange = useCallback(
    (id: string) => (next: boolean) => {
      setOpenFilterGroup(next ? id : null);
    },
    [],
  );
  const [membershipsOpen, setMembershipsOpen] = useState(false);

  const toggleNaceExpanded = (code: string) => {
    setExpandedNaceNodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return {
    cityOpen, setCityOpen, citySearch, setCitySearch, bundeslandOpen, setBundeslandOpen,
    ageOpen, setAgeOpen, naceCodesOpen, setNaceCodesOpen, naceSearch, setNaceSearch,
    expandedNaceNodes, toggleNaceExpanded, naceListRef, industryOpen, setIndustryOpen,
    tagsOpen, setTagsOpen, tagSearch, setTagSearch, cpvCodesOpen, setCpvCodesOpen,
    cpvSearch, setCpvSearch, cpvCodesList, cpvListRef, rechtsformOpen, setRechtsformOpen,
    yearPickerOpen, setYearPickerOpen, registerTypeOpen, setRegisterTypeOpen,
    registerCourtOpen, setRegisterCourtOpen, registerCourtSearch, setRegisterCourtSearch,
    courtList, openFilterGroup, handleGroupOpenChange, membershipsOpen, setMembershipsOpen,
  };
}

interface SidebarOptionsInput {
  country: string;
  facetCount: (facet: string, value: string) => number | undefined;
  facetSum: (facet: string, values: string[]) => number | undefined;
  i18n: I18n;
  fmtLocale: string;
  naceCodes: NaceCode[];
  naceSearch: string;
  expandedNaceNodes: Set<string>;
  tier: FilterTier;
}

/** The option lists the pickers offer (ordered by facet counts) and the tier locks. */
export function useSidebarOptions({
  country,
  facetCount,
  facetSum,
  i18n,
  fmtLocale,
  naceCodes,
  naceSearch,
  expandedNaceNodes,
  tier,
}: SidebarOptionsInput) {
  // The country scope (set via the header's flag selector, synced in through
  // filterParams) drives the location vocabulary: German states, Swiss
  // cantons, Austrian states, or (Global) every offered country's.
  // mv_search.bundesland_name holds canton / Austrian Bundesland names for
  // those rows, so the same backend parameter serves all — only the option
  // list and its label change. Austria joins Global with its search scope.
  const locationOptions = useMemo(() => {
    const cantons = Object.values(SWISS_CANTON_NAMES).sort();
    const options =
      country === 'CH'
        ? cantons
        : isCompanyCountryCode(country)
          ? COMPANY_COUNTRIES[country].regions
          : [
              ...bundeslandList,
              ...(COMPANY_COUNTRIES.AT.searchScope
                ? COMPANY_COUNTRIES.AT.regions
                : []),
              ...cantons,
            ];
    return sortByFacetCount(options, (b) => facetCount('bundesland', b)).map(
      (value) => ({ value, label: swissCantonLabel(value, i18n.language) }),
    );
  }, [country, facetCount, i18n.language]);
  const allIndustries = useMemo(
    () =>
      sortByFacetCount(allIndustryCategoriesSorted(fmtLocale), (c) =>
        facetCount('industry', c.key),
      ),
    [fmtLocale, facetCount],
  );
  // Same treatment for the other unordered vocabularies. Rechtsform sums
  // its codes because one label can map onto several (GmbH is 221110 and
  // the coarse 221100).
  const rechtsformOptions = useMemo(
    () => sortByFacetCount(RECHTSFORM_OPTIONS, (o) => facetSum('rechtsform_code', o.codes)),
    [facetSum],
  );
  const membershipOptions = useMemo(
    () => sortByFacetCount(MEMBERSHIP_OPTIONS, (o) => facetCount('membership', o.value)),
    [facetCount],
  );
  const registerTypeOptions = useMemo(
    () => sortByFacetCount(REGISTER_TYPE_OPTIONS, (o) => facetCount('register_type', o.value)),
    [facetCount],
  );

  // The server strips anything above the tier regardless of what the UI
  // allows (lib/search-gating) — this only decides what to disable and how
  // to explain it.
  const lockOf = useCallback(
    (control: string) => controlLock(control, tier),
    [tier],
  );
  // A group whose every control is out of reach says so on its header. It
  // still opens: the disabled controls inside are what make signing up worth
  // it, so hiding them would sell nothing.
  const groupLock = useCallback(
    (group: string) => groupLockFor(group, tier),
    [tier],
  );

  // Indexes used both for "does this node have children?" (chevron rendering)
  // and for resolving the parent code of an arbitrary node's ancestor in the
  // visibility check below. Built once per dataset.
  const naceByCode = useMemo(() => {
    const m = new Map<string, NaceCode>();
    for (const c of naceCodes) m.set(c.code, c);
    return m;
  }, [naceCodes]);

  const naceCodesHaveChildren = useMemo(() => {
    const parents = new Set<string>();
    for (const c of naceCodes) {
      const p = getNaceParent(c);
      if (p) parents.add(p);
    }
    return parents;
  }, [naceCodes]);

  const sortedNaceCodes = useMemo(
    () =>
      [...naceCodes].sort((a, b) =>
        naceSortKey(a).localeCompare(naceSortKey(b)),
      ),
    [naceCodes],
  );

  // Visible list:
  //  - search active (≥2 chars): flat, every level matching code or description
  //  - otherwise: tree mode — top-level + children of every expanded ancestor
  const filteredNaceCodes = useMemo(
    () => visibleNaceCodes(sortedNaceCodes, naceSearch, expandedNaceNodes, naceByCode),
    [sortedNaceCodes, naceSearch, expandedNaceNodes, naceByCode],
  );

  // Valid financial years offered in the year picker. Backend supports the
  // last several years; we expose 2018 → currentYear.
  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const ys: number[] = [];
    for (let y = now; y >= 2018; y--) ys.push(y);
    return ys;
  }, []);

  return {
    locationOptions, allIndustries, rechtsformOptions, membershipOptions, registerTypeOptions,
    lockOf, groupLock, naceCodesHaveChildren, filteredNaceCodes, yearOptions,
  };
}
